import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportStaff, AirportPassenger } from "../components/index.js";
import { onPad, stationPad } from "../domain/config.js";
import { BREAK_AT, BREAK_POLICIES, ENERGY_DRAIN, ENERGY_RECOVER, RUSH_DRAIN, STAFF_ROOM, shiftsActive } from "../domain/act2.js";
import { boardingWorkPoint } from "../domain/queues.js";
import { tier } from "../domain/airports.js";
import { worksDesk } from "../services/crew.js";
import { rallied } from "../services/act2.js";
import { distance, ids, state, stations } from "../services/world.js";

/**
 * Staff condition from Act II (MGT-003/004/005): training counts down off the floor, desk work drains energy (faster
 * in a rush, half as fast with a captain's rally), and a tired helper finishes the customer in hand before taking a
 * break in the staff room. Nobody needs to be tapped awake; Act I staff never tire.
 */
@System(AirportStaff)
export class AirportStaffCondition extends SystemImpl<GameModel> {
    static depth = 1450;
    run = (g: GameModel, id: number) => {
        const e = state(g), s = g.getTypedUnsafe(AirportStaff, id);
        if (!e.loaded || !shiftsActive(e) || s.role === 'pilot') return;
        if (s.training > 0) {
            if (--s.training === 0) s.skill = Math.min(3, s.skill + 1);
            return;
        }
        if (s.onBreak) {
            if (distance(g.getTypedUnsafe(Transform, id), STAFF_ROOM) < 24) s.energy = Math.min(100, s.energy + ENERGY_RECOVER);
            if (s.energy >= 100) s.onBreak = false;
            return;
        }
        if (!worksDesk(s) || s.stage < 0 || !(s.stage === 6 ? Array.from({ length: stations(g)[6].lanes }, (_, gate) => boardingWorkPoint(gate, tier(e).code)) : [stationPad(s.stage, 'work')]).some(pad => onPad('work', g.getTypedUnsafe(Transform, id), pad))) return;
        const desk = stations(g)[s.stage];
        s.energy = Math.max(0, s.energy - ENERGY_DRAIN * (desk.queueLength >= 8 ? RUSH_DRAIN : 1) * (rallied(g, s.stage) ? 0.5 : 1));
        // Finish the customer in hand first: leave only when the desk has nobody mid-service, or when nearly exhausted.
        const serving = ids(g, AirportPassenger).some(p => { const q = g.getTypedUnsafe(AirportPassenger, p); return q.stage === s.stage && q.state === 2; });
        if (s.energy < (BREAK_POLICIES[e.policyBreak]?.at ?? BREAK_AT) && (!serving || s.energy < 10)) s.onBreak = true;
    };
}
